import * as babylon from "@babylonjs/core";

import type { Measurement } from "../../measure/measureSession";
import type { BabylonState, MeasureVisual, Vector3Plain } from "../../state";

/**
 * The measure tool's accent color, shared by lines, dots, and the anchor.
 * Approximately the design system's magenta-9, as a Babylon literal: CSS
 * variables don't reach into the GL scene.
 */
const MEASURE_COLOR = new babylon.Color3(0.9, 0.28, 0.54);

/**
 * Opacity of the always-on-top copy of each line. Where scene geometry
 * occludes the depth-tested line, this faint copy is all that remains.
 */
const XRAY_ALPHA = 0.3;

/**
 * Babylon rendering group for the always-on-top pass. Groups above 0 clear
 * the depth buffer before drawing by default, so meshes here draw over the
 * scene.
 */
const OVERLAY_RENDERING_GROUP = 1;

/**
 * Dot diameter as a fraction of the camera's orbit radius (the
 * camera-to-focus distance in both camera modes), giving every dot a
 * constant, small screen size at any zoom. The anchor and the endpoints
 * share the rule; screen-constant sizing keeps dots from collapsing to a
 * pixel or swallowing the line at extreme camera distances.
 */
const DOT_DIAMETER_PER_RADIUS = 0.012;

/**
 * Draws the measure tool's state into the scene: one solid + one x-ray
 * line and two endpoint dots per completed measurement, and a dot at the
 * in-progress anchor.
 *
 * Runs every frame, after `applyRenderOrigin` fixes the frame's origin.
 * Measurements are stored in data space, so each visual is re-placed at
 * `point - focusPoint`. Geometry is built once in start-relative
 * coordinates so a frame only writes positions, never vertices.
 *
 * Visual lifetime is reconciled by id against the measure state each frame,
 * not driven off events. Removing a measurement is therefore just a state
 * edit: `BabylonManager.removeMeasurement` and `clearMeasurements` drop
 * measurements, and exactly the meshes and materials that lost their entry
 * go on the next frame, with no disposal wiring of their own.
 */
export function renderMeasures(state: BabylonState): void {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return;
  }
  const scene = state.context.scene;
  const focusPoint = camera.focusPoint;
  const measure = state.input.tool?.measure;
  if (!state.sceneConfig.measureVisuals) {
    state.sceneConfig.measureVisuals = { byId: new Map() };
  }
  const visuals = state.sceneConfig.measureVisuals;

  const measurements = measure?.measurements ?? [];
  const liveIds = new Set(measurements.map((measurement) => measurement.id));

  for (const [id, visual] of visuals.byId) {
    if (!liveIds.has(id)) {
      disposeVisual(visual);
      visuals.byId.delete(id);
    }
  }

  const dotDiameter = camera.babylonCamera.radius * DOT_DIAMETER_PER_RADIUS;
  for (const measurement of measurements) {
    let visual = visuals.byId.get(measurement.id);
    if (!visual) {
      visual = buildVisual(scene, measurement);
      visuals.byId.set(measurement.id, visual);
    }
    placeInRenderSpace(visual.solid, measurement.start, focusPoint);
    placeInRenderSpace(visual.xray, measurement.start, focusPoint);
    placeInRenderSpace(visual.startDot, measurement.start, focusPoint);
    placeInRenderSpace(visual.endDot, measurement.end, focusPoint);
    visual.startDot.scaling.setAll(dotDiameter);
    visual.endDot.scaling.setAll(dotDiameter);
  }

  const anchor =
    measure?.session.phase === "anchored" ? measure.session.start : undefined;
  if (anchor) {
    const dot = (visuals.anchorDot ??= buildDot(scene, "measure:anchor"));
    placeInRenderSpace(dot, anchor, focusPoint);
    dot.scaling.setAll(dotDiameter);
  } else if (visuals.anchorDot) {
    disposeDot(visuals.anchorDot);
    visuals.anchorDot = undefined;
  }
}

/**
 * Puts a mesh where a data-space point lands in this frame's render space:
 * the same `- focusPoint` shift `applyRenderOrigin` gives every other mesh,
 * and the one `reportMeasureOverlay` projects the labels through.
 *
 * Writes into the mesh's own position vector rather than assigning a fresh
 * one: two meshes must never share a vector, and placement then allocates
 * nothing.
 */
function placeInRenderSpace(
  mesh: babylon.Mesh,
  point: Vector3Plain,
  focusPoint: babylon.Vector3,
): void {
  mesh.position.set(
    point.x - focusPoint.x,
    point.y - focusPoint.y,
    point.z - focusPoint.z,
  );
}

function buildVisual(
  scene: babylon.Scene,
  measurement: Measurement,
): MeasureVisual {
  // Start-relative points, so per-frame placement is a position write.
  const points = [
    babylon.Vector3.Zero(),
    new babylon.Vector3(
      measurement.end.x - measurement.start.x,
      measurement.end.y - measurement.start.y,
      measurement.end.z - measurement.start.z,
    ),
  ];
  const solid = babylon.MeshBuilder.CreateLines(
    `measure:${measurement.id}:solid`,
    { points },
    scene,
  );
  solid.color = MEASURE_COLOR;
  solid.isPickable = false;

  // `useVertexAlpha` puts the line in the alpha-blended pass. Without it
  // Babylon leaves blending off, and `xray.alpha` reaches the shader but
  // never blends: this copy would draw at full strength over the scene.
  const xray = babylon.MeshBuilder.CreateLines(
    `measure:${measurement.id}:xray`,
    { points, useVertexAlpha: true },
    scene,
  );
  xray.color = MEASURE_COLOR;
  xray.alpha = XRAY_ALPHA;
  xray.isPickable = false;
  xray.renderingGroupId = OVERLAY_RENDERING_GROUP;

  const startDot = buildDot(scene, `measure:${measurement.id}:start`);
  const endDot = buildDot(scene, `measure:${measurement.id}:end`);
  return { solid, xray, startDot, endDot };
}

/**
 * An opaque dot in the always-on-top pass, marking one measured point.
 * Built at unit diameter; `renderMeasures` rewrites the scaling every frame
 * from the camera's radius.
 */
function buildDot(scene: babylon.Scene, name: string): babylon.Mesh {
  const dot = babylon.MeshBuilder.CreateSphere(
    name,
    { diameter: 1, segments: 8 },
    scene,
  );
  const material = new babylon.StandardMaterial(`${name}:material`, scene);
  material.emissiveColor = MEASURE_COLOR;
  material.disableLighting = true;
  dot.material = material;
  dot.isPickable = false;
  dot.renderingGroupId = OVERLAY_RENDERING_GROUP;
  return dot;
}

function disposeVisual(visual: MeasureVisual): void {
  // A `LinesMesh` disposes the shader material it built for itself; a dot's
  // `StandardMaterial` is module-made and needs explicit disposal.
  visual.solid.dispose();
  visual.xray.dispose();
  disposeDot(visual.startDot);
  disposeDot(visual.endDot);
}

/** Drops a dot and the material `buildDot` gave it. */
function disposeDot(dot: babylon.Mesh): void {
  dot.dispose(false, true);
}
